/**
 * CheckoutModal — W2.6
 *
 * Stripe-Elements-Provider + CardElement für Karteneingabe.
 * Zusammenfassung: Plan, Dauer, Preis.
 * Nach Checkout: onSuccess-Callback.
 * 409: Toast "Du hast bereits ein aktives Abo."
 */
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Stripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import type { Plan } from './useSubscriptionPlans'
import { displayPrice } from './useSubscriptionPlans'
import type { DurationMonths } from './subscriptionUtils'
import { fmtPrice } from '../../lib/fmt'
import { CryptoCheckout } from './CryptoCheckout'
import type { CryptoQuote, Subscription, SubscribeResult } from './useSubscription'
import { AlreadySubscribedError } from './useSubscription'
import { apiErrorStatus } from '../../lib/errors/apiErrorMessage'

type PaymentTab = 'card' | 'crypto'

let stripePromise: Promise<Stripe | null> | null = null

function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    // '/pure': der Default-Entrypoint von @stripe/stripe-js injiziert schon
    // BEIM IMPORT ein <script src="https://js.stripe.com/v3">. Da rolldown
    // (vite.config.ts manualChunks 'stripe') @stripe/react-stripe-js — statisch
    // aus dieser Datei importiert — und @stripe/stripe-js in denselben Chunk
    // legt, wird dieser Chunk vom Entry EAGER geladen: der Seiteneffekt lief
    // dadurch bei JEDEM Boot, auf /login, / und /people. Haengt js.stripe.com /
    // m.stripe.network (EU-Netze, Adblocker, Corporate-Proxy), bleibt der
    // Frame-Load offen -> Screenshots/A11y-Snapshots/Script-Injection laufen in
    // 30-90s-Timeouts. '/pure' laedt das Script erst wenn loadStripe() wirklich
    // aufgerufen wird — also beim Oeffnen dieses Modals.
    stripePromise = import('@stripe/stripe-js/pure').then(({ loadStripe }) =>
      loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '')
    )
  }
  return stripePromise
}

function getStripeTheme(): 'night' | 'stripe' {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'night' : 'stripe'
}

function isStripeTestMode(): boolean {
  return (import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '').startsWith('pk_test')
}

// --- Inner form (needs Stripe context) ---

type CheckoutFormProps = {
  plan: Plan
  duration: DurationMonths
  onSuccess: () => void
  onAlreadySubscribed: () => void
  onClose: () => void
  subscribe: (paymentMethodId: string, planId: string, durationMonths: number) => Promise<SubscribeResult>
  refetchSubscription: () => Promise<{ data: Subscription | null | undefined }>
}

function CheckoutForm({
  plan,
  duration,
  onSuccess,
  onAlreadySubscribed,
  onClose,
  subscribe,
  refetchSubscription,
}: CheckoutFormProps) {
  const { t } = useTranslation('subscription')
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)

  const total = displayPrice(plan, duration)

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return
    setIsLoading(true)
    setCardError(null)

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setIsLoading(false)
      return
    }

    // PaymentMethod (pm_…) statt Legacy-Token: das BE erwartet eine PaymentMethod-ID
    // und schließt bei 3DS/SCA per confirmCardPayment ab.
    const pmResult = await stripe.createPaymentMethod({ type: 'card', card: cardElement })
    if (pmResult.error) {
      setCardError(pmResult.error.message ?? t('checkout.cardError'))
      setIsLoading(false)
      return
    }

    try {
      const res = await subscribe(pmResult.paymentMethod.id, plan.id, duration)
      if (res.requiresAction) {
        // 3DS/SCA-Challenge im Stripe-Overlay abschließen.
        const confirm = await stripe.confirmCardPayment(res.clientSecret)
        if (confirm.error) {
          setCardError(confirm.error.message ?? t('checkout.paymentError'))
          setIsLoading(false)
          return
        }
        // Aktivierung folgt per Webhook — Status neu laden.
        await refetchSubscription()
      }
      onSuccess()
    } catch (err: unknown) {
      // FE-ROB-04: 409 kommt jetzt als typisierte AlreadySubscribedError an
      // (frueher: String-Vergleich gegen 'ALREADY_SUBSCRIBED', der wegen der
      // vorgelagerten ApiError nie zutraf). 429 bekommt eine eigene Meldung,
      // damit der User nicht sofort erneut absendet.
      if (err instanceof AlreadySubscribedError) {
        onAlreadySubscribed()
      } else if (apiErrorStatus(err) === 429) {
        setCardError(t('checkout.rateLimited'))
      } else {
        setCardError(t('checkout.paymentError'))
      }
    } finally {
      setIsLoading(false)
    }
  }, [stripe, elements, plan, duration, subscribe, refetchSubscription, onSuccess, onAlreadySubscribed, t])

  return (
    <div data-testid="checkout-form">
      {/* Zusammenfassung */}
      <div
        style={{
          background: 'var(--surface-elevated, var(--surface))',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 20,
          fontSize: 14,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>{t('checkout.summary.plan')}</span>
          <strong>{plan.name}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>{t('checkout.summary.duration')}</span>
          <strong>{t('duration.month', { count: duration })}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{t('checkout.summary.total')}</span>
          <strong>{fmtPrice(total, plan.currency)}</strong>
        </div>
      </div>

      {/* Stripe CardElement */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '10px 12px',
          marginBottom: 16,
          background: 'var(--input-bg, var(--surface))',
        }}
        data-testid="card-element-wrapper"
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '14px',
                color: 'var(--text)',
                '::placeholder': { color: 'var(--text-subtle)' },
              },
            },
          }}
        />
      </div>

      {isStripeTestMode() && (
        <p
          data-testid="checkout-testmode-hint"
          style={{ color: 'var(--text-subtle)', fontSize: 12, marginBottom: 12 }}
        >
          {t('checkout.testModeHint')}
        </p>
      )}

      {cardError && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {cardError}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose} disabled={isLoading}>
          {t('common:actions.cancel')}
        </button>
        <button
          className="btn btn-primary"
          onClick={() => { void handleSubmit() }}
          disabled={isLoading || !stripe}
          data-testid="checkout-submit"
        >
          {isLoading ? t('checkout.submitButtonLoading') : t('checkout.submitButton')}
        </button>
      </div>
    </div>
  )
}

// --- Modal wrapper ---

type CheckoutModalProps = {
  plan: Plan
  duration: DurationMonths
  onSuccess: () => void
  onAlreadySubscribed: () => void
  onClose: () => void
  subscribe: (paymentMethodId: string, planId: string, durationMonths: number) => Promise<SubscribeResult>
  requestCryptoQuote: (planId: string, durationMonths: number) => Promise<CryptoQuote>
  refetchSubscription: () => Promise<{ data: Subscription | null | undefined }>
}

export function CheckoutModal({
  plan,
  duration,
  onSuccess,
  onAlreadySubscribed,
  onClose,
  subscribe,
  requestCryptoQuote,
  refetchSubscription,
}: CheckoutModalProps) {
  const { t } = useTranslation('subscription')
  const [stripeInstance] = useState<Promise<Stripe | null>>(getStripePromise)
  const [tab, setTab] = useState<PaymentTab>('card')
  const appearance = {
    theme: getStripeTheme(),
    variables: {
      fontFamily: 'var(--font-body, sans-serif)',
      colorPrimary: 'var(--c-primary, #6366f1)',
    },
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('checkout.modalAriaLabel')}
      data-testid="checkout-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        padding: 16,
      }}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 480,
          padding: 24,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ marginBottom: 16, fontSize: 18 }}>{t('checkout.title')}</h2>

        {/* Bezahlart-Auswahl: Kreditkarte (Stripe) oder Crypto (HNT/Solana) */}
        <div
          role="tablist"
          aria-label={t('checkout.paymentTabsAriaLabel')}
          style={{ display: 'flex', gap: 8, marginBottom: 16 }}
          data-testid="payment-method-tabs"
        >
          <button
            role="tab"
            aria-selected={tab === 'card'}
            className={`btn ${tab === 'card' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ flex: 1 }}
            onClick={() => setTab('card')}
            data-testid="tab-card"
          >
            {t('checkout.tabCard')}
          </button>
          <button
            role="tab"
            aria-selected={tab === 'crypto'}
            className={`btn ${tab === 'crypto' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ flex: 1 }}
            onClick={() => setTab('crypto')}
            data-testid="tab-crypto"
          >
            {t('checkout.tabCrypto')}
          </button>
        </div>

        {tab === 'card' ? (
          <Elements stripe={stripeInstance} options={{ appearance: appearance as import('@stripe/stripe-js').Appearance }}>
            <CheckoutForm
              plan={plan}
              duration={duration}
              onSuccess={onSuccess}
              onAlreadySubscribed={onAlreadySubscribed}
              onClose={onClose}
              subscribe={subscribe}
              refetchSubscription={refetchSubscription}
            />
          </Elements>
        ) : (
          <CryptoCheckout
            plan={plan}
            duration={duration}
            onSuccess={onSuccess}
            onClose={onClose}
            requestCryptoQuote={requestCryptoQuote}
            refetchSubscription={refetchSubscription}
          />
        )}
      </div>
    </div>
  )
}
